import {
  ChangeEvent,
  KeyboardEvent,
  RefObject,
  useLayoutEffect,
  useRef,
} from 'react'
import { ImageIcon, Paperclip, Send, Video, X } from 'lucide-react'

import { cn } from '@/app/utils/cn'

import { formatFileSize } from '../useSupportRequestDetailSheet'

interface ChatComposerProps {
  message: string
  setMessage: (value: string) => void
  files: File[]
  fileInputRef: RefObject<HTMLInputElement>
  onSelectFiles: (event: ChangeEvent<HTMLInputElement>) => void
  onRemoveFile: (index: number) => void
  onOpenFilePicker: () => void
  onSend: () => void
  allowAttachments: boolean
  canSubmit: boolean
  isSending: boolean
}

const MAX_TEXTAREA_HEIGHT = 112

export const ChatComposer = ({
  message,
  setMessage,
  files,
  fileInputRef,
  onSelectFiles,
  onRemoveFile,
  onOpenFilePicker,
  onSend,
  allowAttachments,
  canSubmit,
  isSending,
}: ChatComposerProps) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null)

  // o campo cresce com o texto, até algumas linhas
  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    textarea.style.height = 'auto'
    textarea.style.height = `${Math.min(textarea.scrollHeight, MAX_TEXTAREA_HEIGHT)}px`
  }, [message])

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key !== 'Enter' || event.shiftKey) return
    if (event.nativeEvent.isComposing) return

    event.preventDefault()
    if (canSubmit) onSend()
  }

  return (
    <div className="flex flex-col gap-2 border-t border-gray-200 bg-white p-2">
      {files.length > 0 && (
        <div className="flex max-h-28 flex-col gap-1 overflow-y-auto">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center gap-2 rounded-lg border border-gray-300 p-1.5"
            >
              {file.type.startsWith('video/') ? (
                <Video className="h-4 w-4 shrink-0" />
              ) : (
                <ImageIcon className="h-4 w-4 shrink-0" />
              )}
              <div className="flex min-w-0 flex-1 flex-col">
                <span className="truncate text-xs text-gray-700">
                  {file.name}
                </span>
                <span className="text-[10px] text-black04">
                  {formatFileSize(file.size)}
                </span>
              </div>
              <button
                type="button"
                onClick={() => onRemoveFile(index)}
                disabled={isSending}
                aria-label={`Remover ${file.name}`}
                className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*"
        multiple
        className="hidden"
        onChange={onSelectFiles}
      />

      <div className="flex items-end gap-2">
        {allowAttachments && (
          <button
            type="button"
            onClick={onOpenFilePicker}
            disabled={isSending}
            aria-label="Anexar arquivos"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gray-300 text-black03 hover:bg-gray-100"
          >
            <Paperclip className="h-4 w-4" />
          </button>
        )}

        <textarea
          ref={textareaRef}
          rows={1}
          value={message}
          onChange={(event) => setMessage(event.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escreva uma mensagem…"
          disabled={isSending}
          className="max-h-28 min-h-9 flex-1 resize-none rounded-2xl border border-gray-300 bg-white px-3 py-2 text-xs outline-none focus:border-primary disabled:opacity-60"
        />

        <button
          type="button"
          onClick={onSend}
          disabled={!canSubmit}
          aria-label="Enviar mensagem"
          className={cn(
            'flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary text-white',
            !canSubmit && 'opacity-50',
          )}
        >
          <Send className="h-4 w-4" />
        </button>
      </div>
    </div>
  )
}
